/**
 * Edit Line Item – Manual Invoice
 * Used for manual invoice create/update only. Same design as EditLineItemForInvoiceComponent
 * but without "Change this once" / "Change it always" (no GL override flow).
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { Dialog } from "../../../../ui/components/Dialog";
import { TextField } from "../../../../ui/components/TextField";
import { Select } from "../../../../ui/components/Select";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { FeatherInfo, FeatherX } from "@subframe/core";
import { Tax } from "../../../../services/api";
import { Checkbox } from "../../../../ui/components/Checkbox";
import { AccountNameDropdown } from "../../../../shared/presentation/components";
import { GLMappingEntity } from "../../../../shared/domain/entities/GLMappingEntity";
import { taxRepository } from "../../../../shared/data/repositories/TaxRepositoryImpl";
import type { TaxEntity } from "../../../../shared/domain/entities/TaxEntity";

interface EditLineItemManualInvoiceProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingLineItemData: any | null;
  originalAccountName: string;
  editingLineItemIndex: number | null;
  taxes: Tax[];
  customerName?: string;
  customerId?: string;
  isNewItem?: boolean;
  isGstEnabled?: boolean;
  currencySymbol?: string;
  gstTreatment?: string;
  isInterState?: boolean;
  onSave: () => void;
  onCancel: () => void;
  onLineItemDataChange: (data: any) => void;
}

const EditLineItemManualInvoice: React.FC<EditLineItemManualInvoiceProps> = ({
  open,
  onOpenChange,
  editingLineItemData,
  originalAccountName,
  taxes,
  customerName,
  customerId,
  isNewItem = false,
  isGstEnabled = true,
  currencySymbol = "₹",
  gstTreatment,
  isInterState,
  onSave,
  onCancel,
  onLineItemDataChange,
}) => {
  const roundOff = (value: number): string => value.toFixed(2);
  const roundToTwo = (value: number) => parseFloat((value || 0).toFixed(2));

  const [error, setError] = useState<string | null>(null);
  const [rateIncludesTax, setRateIncludesTax] = useState(false);
  const [inclusiveRateSnapshot, setInclusiveRateSnapshot] = useState<number | null>(null);
  const [lineItemTaxes, setLineItemTaxes] = useState<TaxEntity[]>([]);
  const [taxesLoading, setTaxesLoading] = useState(false);

  const quantityInputRef = useRef<HTMLInputElement>(null);
  const rateInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open || !editingLineItemData) return;
    const quantityEl = quantityInputRef.current;
    const rateEl = rateInputRef.current;
    const handler = (e: WheelEvent) => e.preventDefault();
    if (quantityEl) quantityEl.addEventListener("wheel", handler, { passive: false });
    if (rateEl) rateEl.addEventListener("wheel", handler, { passive: false });
    return () => {
      quantityEl?.removeEventListener("wheel", handler);
      rateEl?.removeEventListener("wheel", handler);
    };
  }, [open, editingLineItemData]);

  useEffect(() => {
    if (open) {
      setError(null);
      setRateIncludesTax(false);
      setInclusiveRateSnapshot(null);
    }
  }, [open, originalAccountName, gstTreatment]);

  const isOverseas = gstTreatment === "overseas";

  // Fetch IGST or GST taxes based on inter/intra state when modal opens
  useEffect(() => {
    if (!open || !isGstEnabled) return;
    let mounted = true;
    (async () => {
      setTaxesLoading(true);
      try {
        if (isOverseas) {
          const interResult = await taxRepository.fetchTaxSpecification("inter");
          if (!mounted) return;
          if (interResult.success && interResult.data?.taxes) {
            setLineItemTaxes(interResult.data.taxes.filter((t) => t.is_inactive === "0"));
          } else {
            setLineItemTaxes(taxes as TaxEntity[]);
          }
        } else if (isInterState === true) {
          const interResult = await taxRepository.fetchTaxSpecification("inter");
          if (!mounted) return;
          if (interResult.success && interResult.data?.taxes) {
            setLineItemTaxes(interResult.data.taxes.filter((t) => t.is_inactive === "0"));
          } else {
            setLineItemTaxes(taxes as TaxEntity[]);
          }
        } else if (isInterState === false) {
          const intraResult = await taxRepository.fetchTaxSpecification("intra");
          if (!mounted) return;
          if (intraResult.success && intraResult.data?.taxes) {
            setLineItemTaxes(intraResult.data.taxes.filter((t) => t.is_inactive === "0"));
          } else {
            setLineItemTaxes(taxes as TaxEntity[]);
          }
        } else {
          const [interResult, intraResult] = await Promise.all([
            taxRepository.fetchTaxSpecification("inter"),
            taxRepository.fetchTaxSpecification("intra"),
          ]);
          if (!mounted) return;
          const all: TaxEntity[] = [];
          if (interResult.success && interResult.data?.taxes) {
            all.push(...interResult.data.taxes.filter((t) => t.is_inactive === "0"));
          }
          if (intraResult.success && intraResult.data?.taxes) {
            all.push(...intraResult.data.taxes.filter((t) => t.is_inactive === "0"));
          }
          const unique = all.filter((t, i, self) => self.findIndex((x) => x.tax_id === t.tax_id) === i);
          setLineItemTaxes(unique);
        }
      } catch {
        if (mounted) setLineItemTaxes(taxes as TaxEntity[]);
      } finally {
        if (mounted) setTaxesLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [open, isGstEnabled, isInterState, isOverseas, taxes]);

  const handleAccountSelection = (account: GLMappingEntity) => {
    const accountId = account.account_id;
    onLineItemDataChange({
      ...editingLineItemData,
      account_name: account.gl_name,
      account_id: accountId,
    });
  };

  const handleTaxSelection = (selectedTaxId: string) => {
    const taxList = lineItemTaxes.length > 0 ? lineItemTaxes : (taxes as TaxEntity[]);
    if (selectedTaxId === "NO_TAX") {
      const quantity = editingLineItemData.quantity || 0;
      const rate = roundToTwo(Number(editingLineItemData.rate) || 0);
      const itemTotal = quantity * rate;
      onLineItemDataChange({
        ...editingLineItemData,
        tax_id: "NO_TAX",
        tax_name: "",
        tax_percentage: 0,
        tax_amount: 0,
        item_total: itemTotal,
        item_total_inclusive_of_tax: itemTotal,
      });
      return;
    }
    const selectedTax = taxList.find((t) => t.tax_id === selectedTaxId);
    if (selectedTax) {
      const taxPercentage = parseFloat(selectedTax.tax_percentage);
      const quantity = editingLineItemData.quantity || 0;
      let rate = roundToTwo(editingLineItemData.rate || 0);
      if (rateIncludesTax && inclusiveRateSnapshot !== null && isGstEnabled) {
        rate = roundToTwo(inclusiveRateSnapshot / (1 + taxPercentage / 100));
      }
      const itemTotal = quantity * rate;
      const taxAmount = (itemTotal * taxPercentage) / 100;
      let taxName = selectedTax.tax_name;
      if (!taxName?.trim()) {
        if (selectedTax.tax_id && /^[A-Z]+(\d+)$/i.test(selectedTax.tax_id)) taxName = selectedTax.tax_id.toUpperCase();
        else if (selectedTax.tax_specific_type) taxName = `${selectedTax.tax_specific_type.toUpperCase()}${Math.round(taxPercentage)}`;
        else if (selectedTax.tax_type) taxName = `${selectedTax.tax_type.toUpperCase()}${Math.round(taxPercentage)}`;
        else taxName = selectedTax.tax_id || "";
      }
      onLineItemDataChange({
        ...editingLineItemData,
        rate,
        tax_id: selectedTax.tax_id,
        tax_name: taxName,
        tax_percentage: taxPercentage,
        tax_amount: taxAmount,
        item_total_inclusive_of_tax: itemTotal + taxAmount,
      });
    }
  };

  const handleRateIncludesTaxToggle = (checked: boolean) => {
    const currentRate = editingLineItemData.rate || 0;
    const taxPercentage = editingLineItemData.tax_percentage || 0;
    const quantity = editingLineItemData.quantity || 0;
    if (checked) {
      setRateIncludesTax(true);
      setInclusiveRateSnapshot(currentRate);
      if (isGstEnabled && taxPercentage > 0) {
        const baseRate = roundToTwo(currentRate / (1 + taxPercentage / 100));
        const itemTotal = quantity * baseRate;
        const taxAmount = (itemTotal * taxPercentage) / 100;
        onLineItemDataChange({
          ...editingLineItemData,
          rate: baseRate,
          item_total: itemTotal,
          tax_amount: taxAmount,
          item_total_inclusive_of_tax: itemTotal + taxAmount,
        });
      }
      return;
    }
    const rateToRestore = roundToTwo(inclusiveRateSnapshot ?? currentRate);
    const itemTotal = quantity * rateToRestore;
    const taxAmount = isGstEnabled ? (itemTotal * taxPercentage) / 100 : 0;
    setRateIncludesTax(false);
    setInclusiveRateSnapshot(null);
    onLineItemDataChange({
      ...editingLineItemData,
      rate: rateToRestore,
      item_total: itemTotal,
      tax_amount: taxAmount,
      item_total_inclusive_of_tax: isGstEnabled ? itemTotal + taxAmount : itemTotal,
    });
  };

  const handleFieldChange = (field: string, value: any) => {
    let updatedData = { ...editingLineItemData, [field]: value };
    if (field === "description") updatedData.name = value;
    if (field === "quantity" || field === "rate") {
      const quantity = field === "quantity" ? value : editingLineItemData.quantity || 0;
      const rate = field === "rate" ? roundToTwo(value) : roundToTwo(editingLineItemData.rate || 0);
      const itemTotal = quantity * rate;
      const taxPercentage = editingLineItemData.tax_percentage || 0;
      const taxAmount = (itemTotal * taxPercentage) / 100;
      if (rateIncludesTax && isGstEnabled) setInclusiveRateSnapshot(rate * (1 + taxPercentage / 100));
      updatedData = {
        ...updatedData,
        item_total: itemTotal,
        tax_amount: isGstEnabled ? taxAmount : 0,
        item_total_inclusive_of_tax: isGstEnabled ? itemTotal + taxAmount : itemTotal,
      };
    }
    onLineItemDataChange(updatedData);
  };

  const isFormValid = () => {
    if (!editingLineItemData) return false;
    const hasDescription = (editingLineItemData.description ?? editingLineItemData.name ?? "").toString().trim().length > 0;
    const hasQuantity = (editingLineItemData.quantity ?? 0) > 0;
    const hasRate = (editingLineItemData.rate ?? 0) > 0;
    const hasAccount = (editingLineItemData.account_id ?? "").toString().trim().length > 0;
    const taxId = (editingLineItemData.tax_id ?? "").toString().trim();
    const hasTax = isGstEnabled
      ? isOverseas
        ? taxId.length > 0 && taxId !== "NO_TAX"
        : taxId.length > 0 || taxId === "" || editingLineItemData.tax_id === "NO_TAX"
      : true;
    return hasDescription && hasQuantity && hasRate && hasAccount && hasTax;
  };

  const handleSave = () => {
    onSave();
  };

  const handleCancel = () => {
    onCancel();
  };

  if (!editingLineItemData) return null;

  const descriptionValue = (editingLineItemData.description ?? editingLineItemData.name ?? "").toString();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="min-w-[600px] max-w-[700px]">
        <div className="flex w-full items-center justify-between gap-4 p-6 border-b border-solid border-neutral-border">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {isNewItem ? "Add Line Item" : "Edit Line Item"}
            </span>
            <span className="text-body font-body text-subtext-color">
              {isNewItem ? "Add a new line item to this invoice" : "Update the details for this line item"}
            </span>
          </div>
          <IconButton size="small" icon={<FeatherX />} onClick={handleCancel} />
        </div>

        <div className="flex w-full flex-col items-start gap-6 px-6 py-6">
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-default-font">Description *</span>
            <TextField className="h-auto w-full flex-none" label="" helpText="">
              <TextField.Input
                placeholder="46B x4R SPB Book Ptg (NS) 70GSM 51 X 76 Cms (1B X 4R ) kgs Batch : Primary Batch"
                value={descriptionValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFieldChange("description", e.target.value)
                }
              />
            </TextField>
          </div>

          <div className="flex w-full items-start gap-6 flex-1">
            <TextField className="h-auto grow shrink-0 basis-0" label="Quantity *" helpText="">
              <TextField.Input
                ref={quantityInputRef}
                type="number"
                step="any"
                placeholder="0.00"
                value={editingLineItemData.quantity?.toString() ?? ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  handleFieldChange("quantity", value === "" ? 0 : parseFloat(value) || 0);
                }}
              />
            </TextField>
            <TextField className="h-auto grow shrink-0 basis-0" label="Rate *" helpText="">
              <TextField.Input
                ref={rateInputRef}
                type="number"
                step="any"
                placeholder="0.00"
                value={editingLineItemData.rate?.toString() ?? ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  handleFieldChange("rate", value === "" ? 0 : parseFloat(value) || 0);
                }}
              />
            </TextField>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox checked={rateIncludesTax} onCheckedChange={(c) => handleRateIncludesTaxToggle(Boolean(c))} />
            <span className="text-caption font-caption text-subtext-color">Rate includes tax (strip tax from rate)</span>
          </div>

          <div className="flex w-full flex-col items-start gap-6">
            <div className="flex w-full flex-col gap-1">
              <span className="text-caption-bold font-caption-bold text-default-font">Account Name *</span>
              <AccountNameDropdown
                docType="invoice"
                selectedAccountId={editingLineItemData.account_id}
                selectedAccountName={editingLineItemData.account_name}
                onSelect={handleAccountSelection}
                dropdownClassName="w-[var(--radix-dropdown-menu-trigger-width)] min-w-full"
              />
            </div>
          </div>

          <div className="flex w-full items-start gap-6 flex-1">
            <TextField className="h-auto grow shrink-0 basis-0" label="HSN/SAC Code" helpText="Optional">
              <TextField.Input
                placeholder="48025790"
                value={editingLineItemData.hsn_or_sac ?? ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange("hsn_or_sac", e.target.value)}
              />
            </TextField>
            {isGstEnabled && (
              <Select
                className="h-auto grow shrink-0 basis-0"
                label="Tax Rate (%) *"
                helpText={isOverseas ? "Select IGST rate for overseas customer" : "Select tax rate"}
                value={
                  editingLineItemData.tax_id && editingLineItemData.tax_id !== ""
                    ? editingLineItemData.tax_id
                    : isOverseas
                      ? ""
                      : "NO_TAX"
                }
                onValueChange={handleTaxSelection}
                placeholder={taxesLoading ? "Loading..." : "Select tax rate"}
                disabled={taxesLoading}
              >
                {!isOverseas && <Select.Item value="NO_TAX">NO TAX</Select.Item>}
                {(lineItemTaxes.length > 0 ? lineItemTaxes : taxes).map((tax) => (
                  <Select.Item key={tax.tax_id} value={tax.tax_id}>
                    {tax.tax_name} ({tax.tax_percentage}%)
                  </Select.Item>
                ))}
              </Select>
            )}
          </div>

          <div className="flex w-full items-center justify-between rounded-md bg-success-50 px-4 py-3">
            <span className="text-body-bold font-body-bold text-default-font">
              Item Total {isGstEnabled ? "(including tax)" : ""}
            </span>
            <span className="text-heading-3 font-heading-3 text-success-600">
              {currencySymbol}
              {(() => {
                const subtotal = editingLineItemData.item_total || 0;
                const taxAmount = (subtotal * (editingLineItemData.tax_percentage || 0)) / 100;
                return isGstEnabled ? roundOff(subtotal + taxAmount) : roundOff(subtotal);
              })()}
            </span>
          </div>
        </div>

        {error && (
          <div className="flex w-full flex-col items-start gap-2 px-6">
            <div className="flex w-full items-start gap-3 rounded-md border border-solid border-error-300 bg-error-100 px-3 py-3">
              <FeatherInfo className="text-body font-body text-error-700" />
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                <span className="text-body-bold font-body-bold text-error-700">Error</span>
                <span className="text-body font-body text-error-700">{error}</span>
              </div>
            </div>
          </div>
        )}
        <div className="flex w-full items-center justify-end gap-3 border-t border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <Button variant="neutral-secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="brand-primary" onClick={handleSave} disabled={isNewItem && !isFormValid()}>
            {isNewItem ? "Add Item" : "Save Changes"}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

export default EditLineItemManualInvoice;
